import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, CheckCircle, Award, Container, Cylinder, PackagePlus } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { useLanguage } from '../contexts/LanguageContext';
import { productDetailsData } from '../productDetailsData';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  const productId = parseInt(id);
  const productData = productDetailsData[productId];
  
  if (!productData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Ürün bulunamadı</h2>
          <Button onClick={() => navigate('/')}>Ana Sayfaya Dön</Button>
        </div>
      </div>
    );
  }

  const productInfo = {
    1: {
      name: t.products.product1.name,
      description: t.products.product1.description,
      apps: t.products.product1.apps
    },
    2: {
      name: t.products.product2.name,
      description: t.products.product2.description,
      apps: t.products.product2.apps
    },
    3: {
      name: t.products.product3.name,
      description: t.products.product3.description,
      apps: t.products.product3.apps
    }
  }[productId];

  const technicalDetails = productData.technicalDetails[language];
  const seoData = productData.seo[language === 'ar' ? 'tr' : language === 'ru' ? 'en' : language];

  const packagingConfig = {
    ibcTank: {
      icon: Container,
      color: "text-blue-600",
      bgColor: "bg-blue-50"
    },
    barrel: {
      icon: Cylinder,
      color: "text-orange-600",
      bgColor: "bg-orange-50"
    },
    kraftBag: {
      icon: PackagePlus,
      color: "text-green-600",
      bgColor: "bg-green-50"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href={`https://www.meskim.com.tr/product/${productId}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content={`https://www.meskim.com.tr/product/${productId}`} />
        <meta property="og:type" content="product" />
        <meta property="og:image" content={productData.image} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={productData.image} />
      </Helmet>
      {/* Header Spacer */}
      <div className="h-20"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/#products')}
          className="mb-8 hover:text-teal-600"
        >
          <ArrowLeft className="mr-2" size={20} />
          {t.products.backToProducts}
        </Button>

        <div className="grid lg:grid-cols-2 gap-12 mb-12">
          {/* Product Image */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={productData.image}
                alt={productInfo.name}
                className="w-full h-96 object-cover"
              />
            </div>
            
            {/* Badges */}
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              <Badge className="bg-green-600 text-white px-4 py-2 text-sm font-semibold shadow-lg">
                <Award className="mr-2" size={16} />
                {t.products.productionBadge}
              </Badge>
              {productData.isFirstProducer && (
                <Badge className="bg-blue-600 text-white px-4 py-2 text-sm font-semibold shadow-lg">
                  <CheckCircle className="mr-2" size={16} />
                  {t.products.firstProducer}
                </Badge>
              )}
            </div>
          </div>

          {/* Product Info */}
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {productInfo.name}
            </h1>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              {productInfo.description}
            </p>

            {/* National Technology Badge */}
            <Card className="p-6 mb-6 bg-gradient-to-r from-red-50 to-white border-2 border-red-200">
              <div className="flex items-center space-x-4">
                <div className="text-5xl">🇹🇷</div>
                <div>
                  <h3 className="text-lg font-bold text-red-900 mb-1">
                    {t.products.nationalTech}
                  </h3>
                  <p className="text-sm text-red-700">
                    %100 yerli sermaye ile Türkiye'de üretilmektedir
                  </p>
                </div>
              </div>
            </Card>

            {/* Applications */}
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t.products.usageAreas}
              </h3>
              <div className="flex flex-wrap gap-2">
                {productInfo.apps.map((app, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="bg-teal-50 text-teal-700 hover:bg-teal-100 text-sm px-3 py-1"
                  >
                    {app}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Technical Specifications */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          <Card className="p-8 shadow-lg border-t-4 border-teal-600">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <CheckCircle className="mr-3 text-teal-600" size={28} />
              {t.products.technicalSpecs}
            </h2>
            <div className="space-y-4">
              {Object.entries(technicalDetails).map(([key, value]) => (
                <div key={key} className="flex justify-between items-start border-b border-gray-200 pb-3">
                  <span className="font-semibold text-gray-700 capitalize">
                    {key === 'formula' ? 'Kimyasal Formül' :
                     key === 'casNumber' ? 'CAS No' :
                     key === 'appearance' ? 'Görünüm' :
                     key === 'concentration' ? 'Konsantrasyon' :
                     key === 'density' ? 'Yoğunluk' :
                     key === 'ph' ? 'pH' :
                     key === 'solubility' ? 'Çözünürlük' :
                     key === 'purity' ? 'Saflık' :
                     key === 'melting' ? 'Erime Noktası' :
                     key === 'storage' ? 'Depolama' :
                     key === 'types' ? 'Türler' : key}:
                  </span>
                  <span className="text-gray-900 text-right">{value}</span>
                </div>
              ))}
            </div>
          </Card>

          {/* Packaging Options */}
          <Card className="p-8 shadow-lg border-t-4 border-blue-600">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Package className="mr-3 text-blue-600" size={28} />
              {t.products.packaging}
            </h2>
            <div className="space-y-4">
              {productData.packaging.map((pack, index) => {
                const config = packagingConfig[pack];
                const IconComponent = config.icon;
                return (
                  <div
                    key={index}
                    className={`flex items-center space-x-4 p-4 ${config.bgColor} rounded-lg hover:shadow-md transition-all border-2 border-transparent hover:border-${config.color.replace('text-', 'border-')}`}
                  >
                    <div className={`w-12 h-12 flex items-center justify-center rounded-lg bg-white shadow-sm`}>
                      <IconComponent className={config.color} size={28} />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">
                        {t.products.packagingTypes[pack]}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded">
              <p className="text-sm text-yellow-800">
                <strong>Not:</strong> Özel ambalaj talepleri için lütfen bizimle iletişime geçin.
              </p>
            </div>
          </Card>
        </div>

        {/* CTA Section */}
        <Card className="p-8 bg-gradient-to-r from-teal-600 to-teal-700 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ürünlerimiz hakkında daha fazla bilgi almak ister misiniz?
          </h2>
          <p className="text-teal-100 mb-6 max-w-2xl mx-auto">
            Teknik detaylar, fiyat bilgisi ve numune talepleri için bizimle iletişime geçin.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/#contact')}
            className="bg-white text-teal-700 hover:bg-gray-100 px-8 py-6 text-lg"
          >
            İletişime Geçin
          </Button>
        </Card>
      </div>
    </div>
  );
};

export default ProductDetail;
